/* eslint-disable node/no-process-env */
import { NextRequest, NextResponse } from 'next/server'

import { routes } from '@routes'
import {
  AUTH_COOKIE_TOKEN,
  NEXT_LOCALE_COOKIE,
  SECURE_AUTH_COOKIE_TOKEN,
  decodeUnverifiedToken,
  isPermittedDestination,
} from '@lib/auth'

/**
 * Key of url query parameters we will use to pass a redirection url
 */
export const REDIRECT_DESTINATION_QUERY = 'redirect'

/**
 * Arguments shape for authRedirect
 */
type AuthRedirectProps = {
  authedDestination?: string
  notAuthedDestination?: string
  /**
   * If set we will forward the requesting url via a query parameter
   * with this name
   */
  forwardUrlKey?: string
}

/**
 * Determine whether we have sufficient authentication information
 * available and return a redirect instruction appropriately given
 * destinations
 */
export const authRedirect = ({
  authedDestination,
  notAuthedDestination,
  forwardUrlKey,
}: AuthRedirectProps) => {
  return (req: NextRequest) => {
    // For legacy cookies
    const localeCookie = req.cookies.get(NEXT_LOCALE_COOKIE)?.toLowerCase()
    const token = decodeUnverifiedToken(
      req.cookies.get(SECURE_AUTH_COOKIE_TOKEN) ??
        req.cookies.get(AUTH_COOKIE_TOKEN)
    )

    const urlRedirect = req.nextUrl.searchParams.get('redirect')

    const destination = token
      ? urlRedirect ?? authedDestination
      : notAuthedDestination

    // skip redirect if destination is not secure
    // redirect if we have resolved a destination, authed or not
    if (destination && isPermittedDestination(destination)) {
      // if the destination is already fully qualified (ie. includes a protocol)
      // then just redirect to that
      if (destination.includes('://')) {
        return NextResponse.redirect(destination)
      }

      // pass the current url as a redirect query parameter so, if typically endpoints
      // know where they could send users back after resolving
      const url = req.nextUrl.clone()
      url.pathname = destination
      if (localeCookie && url.locale !== localeCookie) url.locale = localeCookie
      url.search = forwardUrlKey
        ? `${new URLSearchParams([[forwardUrlKey, req.url]])}`
        : ''

      return NextResponse.redirect(url)
    }

    // Redirect if locale doesn't match cookie
    if (localeCookie && req.nextUrl.locale !== localeCookie) {
      const url = req.nextUrl.clone()
      url.locale = localeCookie
      return NextResponse.redirect(url)
    }

    // otherwise we have nothing to do
    return NextResponse.next()
  }
}

/**
 * A simple middleware which will redirect to the sign-in page if we
 * dont find a auth token
 */
export const privateRedirect = authRedirect({
  notAuthedDestination: routes.Routes.signIn,
  forwardUrlKey: REDIRECT_DESTINATION_QUERY,
})

/**
 * Re-export routes as we have it here and its typically wanted when
 * deciding where to route based on authentication
 */
export { routes }
